import type { LegacyCartResponse, Result as SearchProduct } from "@/generated/api/types.gen"
import { centsToDollars } from "@/lib/helpers/money"
import { type Money, moneyToNumber } from "@/lib/money"
import { toPriceView } from "@/lib/money/price-view"
import type { StoreProduct, StoreProductVariant } from "@/platform/api/store-contract"

import type { GA4Item } from "./types/google"

interface GA4ItemOptions {
  index?: number
  item_list_id?: string
  item_list_name?: string
}

function calculateProductStock(product?: StoreProduct | null) {
  if (!product?.variants) return {}
  const variants = product.variants
  const inStockVariants = variants.filter((v) => (v.inventory_quantity || 0) > 0 || v.allow_backorder)
  return {
    in_stock: inStockVariants.length > 0,
    total_variants: variants.length || undefined,
    in_stock_variants: inStockVariants.length || undefined,
  }
}

function mapCategoriesToGA4(categories: string[]) {
  return {
    item_category: categories[0],
    item_category2: categories[1],
    item_category3: categories[2],
    item_category4: categories[3],
    item_category5: categories[4],
  }
}

export function buildGA4ItemFromProduct(product: StoreProduct, variant?: StoreProductVariant | null, options?: GA4ItemOptions): GA4Item {
  const categories = (product.categories ?? []).map((c) => c.name || c.id)
  const v = variant || product.variants?.[0]
  const variantDescription = v?.options
    ?.map((opt) => opt.value)
    .filter(Boolean)
    .join(" - ")
  const resolved = toPriceView(v?.price ?? product.price)
  const legacyPrice = v?.calculated_price || 0
  const legacyOriginal = v?.original_price || 0
  const legacyDiscount = legacyOriginal > legacyPrice ? centsToDollars(legacyOriginal - legacyPrice) : undefined

  return {
    item_id: product.id || "",
    item_name: product.title || "",
    price: resolved ? moneyToNumber(resolved.amount) : centsToDollars(legacyPrice),
    quantity: 1,
    discount: resolved ? (resolved.savings ? moneyToNumber(resolved.savings) : undefined) : legacyDiscount,
    item_variant: variantDescription || undefined,
    item_variant_id: v?.id || undefined,
    ...mapCategoriesToGA4(categories),
    ...calculateProductStock(product),
    index: options?.index,
    item_list_id: options?.item_list_id,
    item_list_name: options?.item_list_name,
  }
}

export function buildGA4ItemFromSearchProduct(searchProduct: SearchProduct, options?: GA4ItemOptions): GA4Item {
  const categories = searchProduct.categories || []
  const resolved = toPriceView(searchProduct.resolvedPrice)
  const legacyPrice = searchProduct.price ?? 0
  const price = resolved ? moneyToNumber(resolved.amount) : (searchProduct.reducedPrice ?? legacyPrice)
  const discount = resolved
    ? resolved.savings
      ? moneyToNumber(resolved.savings)
      : undefined
    : searchProduct.reducedPrice != null
      ? legacyPrice - searchProduct.reducedPrice
      : undefined

  return {
    item_id: searchProduct.productGroupUuid,
    item_name: searchProduct.name,
    price,
    quantity: 1,
    discount,
    in_stock: searchProduct.inStock ?? undefined,
    ...mapCategoriesToGA4(categories),
    index: options?.index,
    item_list_id: options?.item_list_id,
    item_list_name: options?.item_list_name,
  }
}

export const CURRENCY_MXN = "MXN"

export const priceEventParams = (price: Money | null | undefined) => ({ currency: price?.currency ?? CURRENCY_MXN, value: price ? moneyToNumber(price) : 0 })

export function buildGA4ItemsFromCartV2(cart: LegacyCartResponse, product?: StoreProduct | null): GA4Item[] {
  const categories = (product?.categories ?? []).map((c) => c.name || c.id)

  return cart.items.map((item, index) => ({
    item_id: item.productId,
    item_name: item.title || "",
    price: item.unitPrice || 0,
    quantity: item.quantity || 1,
    discount: item.discountTotal > 0 ? item.discountTotal / item.quantity : undefined,
    item_variant: item.description || undefined,
    item_variant_id: item.variantId || undefined,
    ...mapCategoriesToGA4(categories),
    ...calculateProductStock(product),
    index,
  }))
}
